import React, { useState } from "react";
import VisitorCounter from "./components/VisitorCounter";
import {
  FaLinkedin,
  FaGithub,
  FaEnvelope,
  FaPhone,
  FaShieldAlt,
} from "react-icons/fa";

const Resume = () => {
  const [activeSection, setActiveSection] = useState("all");

  const sections = [
    "all",
    "experience",
    "research",
    "conferences",
    "education",
    "skills",
    "projects",
  ];

  const personalInfo = {
    name: "ANTONIOS ARMONIS",
    email: "ant.armonis@gmail.com",
    phone: "+30 6930796757",
    linkedin: "www.linkedin.com/in/antonios-armonis-304544104",
    github: "github.com/aarmonis",
    military: "Military Service: Completed",
  };

  const education = [
    {
      institution: "The American College of Greece",
      location: "Athens, Greece",
      degree: "BA in Economics",
      period: "Sept. 2019 – May 2025",
      details: [
        {
          text: 'Dissertation: "Modeling Loan Transitions of Single-Family US Mortgages"',
          link: "https://accuria.com/machine-learning-for-transitions-and-modifications-in-us-single-family-mortgages/",
        },
      ],
    },
    {
      institution: "University College London",
      location: "London, United Kingdom",
      degree: "MBBS Medicine – Completed pre-clinical years before exiting",
      period: "Sept. 2014 – Aug. 2019",
      details: [],
    },
  ];

  const experience = [
    {
      company: "Accuria Ltd",
      location: "London, UK",
      position: "AI Engineer",
      period: "Sep. 2024 – Present",
      achievements: [
        "Built and maintained data pipelines in R and Python (DuckDB, AWS S3) for analytics and ESMA regulatory reporting across 2B+ loan-level records.",
        "Implemented AI-powered document analysis pipelines for automated covenant extraction and monitoring.",
        "Co-designed the AI Data Agent, a conversational assistant for automated data onboarding, portfolio stratification, and custom analysis.",
        "Integrated backend APIs and frontend components across several modules of the Accuria platform.",
        "Led internal AI initiatives for marketing automation, including lead generation, contact enrichment, and content production.",
      ],
    },
    {
      company: "UNI-PHARMA Pharmaceutical Laboratories S.A.",
      location: "Athens, Greece",
      position: "Intern Data Analyst at Validation Department",
      period: "June 2022 – Aug. 2022",
      achievements: [
        "Built QA dashboards and production trend visualizations in Python (Seaborn, Highcharter).",
        "Cleaned complex datasets and performed OCR to digitize production logs.",
      ],
    },
    {
      company: "Ulysses-Systems",
      location: "Piraeus, Greece",
      position: "Intern Software Developer",
      period: "June 2021 – Aug 2021",
      achievements: [
        "Labeled datasets for ML-driven email classification models.",
        "Prepared live software demos, enhancing client engagement.",
      ],
    },
  ];

  const research = [
    {
      organization: "Experimental Economics, Fintech, & Analytics Lab",
      position: "Technical Lead",
      period: "Aug 2024 – Present",
      details: [
        "Built a loan transition framework on Fannie Mae data to model borrower state changes and estimate cure/default dynamics using statistical and ML techniques.",
        "Lead the development of Nimbus, an agentic RAG chatbot for student club discovery that replaced siloed information channels with instant conversational search; withstood prompt injection attempts on launch day with zero breaches. Presented at EmTech Europe 2026.",
        "Developed ARIA, a human-in-the-loop agent that automates degree registration decisions by ingesting student emails, pulling transcripts, and drafting rulings; saved ~125 work hours per administrator per enrollment cycle. Presented at EmTech Europe 2026.",
        "Co-authored and manage the SAILOK research project, exploring AI-driven behavioral nudging to reduce irrational debt aversion.",
        "Applied transformer-based NLP to analyze sentiment and thematic shifts in Coldplay lyrics.",
      ],
    },
  ];

  const projects = [
    {
      name: "Nimbus – Student Clubs RAG Agent",
      description:
        "Agentic RAG chatbot that replaced siloed information channels with instant conversational search for university student clubs. Equipped with lookup, analysis, and comparison tools. Features decentralized data ownership where club managers update their own content. Withstood prompt injection attempts on launch day with zero breaches. Deployed in production.",
      link: "https://ambitious-glacier-044146803.1.azurestaticapps.net/",
    },
    {
      name: "ARIA – Administrative Automation Agent",
      description:
        "Human-in-the-loop agent that automates degree registration decisions by ingesting student emails, pulling transcripts, and applying business logic to draft rulings. Built with an abstract architecture to serve all degree programs. Saved ~125 work hours per administrator per enrollment cycle.",
      link: null,
    },
    {
      name: "Athex Financial Retriever",
      description:
        "RAG chatbot for querying financial statements from Athens Stock Exchange listed companies. Embeds chunked financial documents into a vector store for semantic retrieval, combines dense vector search with keyword-based BM25 ranking via hybrid search, and feeds relevant context to a locally hosted Llama model for grounded, citation-backed answers.",
      link: null,
    },
    {
      name: "SAILOK – AI-Driven Mortgage Decision Support",
      description:
        'Building a GenAI-powered decision support tool to tackle irrational debt aversion in Greek households. Segments users into "borrower avatars" via ML clustering (K-Means, GMM) on debt aversion, risk tolerance, and financial literacy scores. A dual-database architecture—SQL MacroDB for macroeconomic indicators and a vector store for regulatory texts—feeds a generative AI module that dynamically adapts the sequence and framing of mortgage information to each user\'s profile. Co-authored the proposal and currently managing development.',
      link: null,
    },
    {
      name: "Coldplay NLP",
      description:
        "Transformer-based NLP analysis of sentiment and thematic shifts across Coldplay's discography.",
      link: "https://github.com/aarmonis/Coldplay_NLP",
    },
    {
      name: "Mortgage Loan Transitions",
      description:
        "Predictive model integrating a first-order Markov process, auto-regressive components, and logistic regression to quantify borrower transitions between performing, arrears, default, modification, and foreclosure states. Built on Fannie Mae data spanning 2001–2021 origination vintages, incorporating borrower-level characteristics and macroeconomic indicators (unemployment, HPI). Published on Accuria Insights.",
      link: "https://accuria.com/machine-learning-for-transitions-and-modifications-in-us-single-family-mortgages/",
    },
  ];

  const skills = {
    languages: ["Python", "R", "SQL"],
    libraries: [
      "FastAPI",
      "PyTorch",
      "scikit-learn",
      "Pydantic",
      "LangChain",
      "Bedrock",
      "pandas",
      "NumPy",
      "dplyr",
      "data.table",
      "DBI",
      "Shiny",
    ],
    Technologies: [
      "NLP",
      "GitHub Actions",
      "AWS Cloud Services",
      "DuckDB",
      "PyTorch",
      "LangChain",
      "Pydantic",
    ],
    certifications: [
      {
        name: "Google Advanced Data Analytics Certificate",
        link: "https://www.coursera.org/account/accomplishments/professional-cert/AHZGLQ4LXPS5?utm_source=ln&utm_medium=certificate&utm_content=cert_image&utm_campaign=pdf_header_button&utm_product=prof",
      },
    ],
  };

  const conferences = [
    {
      name: "EmTech Europe 2026",
      location: "Athens, GR",
      role: 'Speaker – "The Future of Higher Education"',
      period: "Mar 2026",
      description:
        "Co-presented a framework for transforming legacy-heavy institutions into AI-first organizations through rapid PoC cycles, end-user validation, and agentic automation case studies.",
      link: "https://www.kathimerini.gr/life/technology/564135997/to-amerikaniko-kolegio-elladas-os-ypodeigma-ai-first-idrymatos/",
    },
  ];

  const activities = ["Running", "Agriculture", "Travel", "Reading"];

  const shouldShowSection = (section) => {
    return activeSection === "all" || activeSection === section;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-900 text-white p-6">
          <h1 className="text-3xl font-bold">{personalInfo.name}</h1>

          {/* Contact info with icons */}
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <div className="flex items-center">
              <FaPhone className="mr-2 text-blue-200" size={16} />
              <span>{personalInfo.phone}</span>
            </div>
            <div className="flex items-center">
              <FaEnvelope className="mr-2 text-blue-200" size={16} />
              <a
                href={`mailto:${personalInfo.email}`}
                className="hover:underline hover:text-blue-200"
              >
                {personalInfo.email}
              </a>
            </div>
            <div className="flex items-center">
              <FaLinkedin className="mr-2 text-blue-200" size={18} />
              <a
                href={`https://${personalInfo.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline hover:text-blue-200"
              >
                LinkedIn
              </a>
            </div>
            <div className="flex items-center">
              <FaGithub className="mr-2 text-blue-200" size={18} />
              <a
                href={`https://${personalInfo.github}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline hover:text-blue-200"
              >
                GitHub
              </a>
            </div>
            <div className="flex items-center">
              <FaShieldAlt className="mr-2 text-blue-200" size={16} />
              <span>{personalInfo.military}</span>
            </div>
          </div>

          {/* Add the VisitorCounter component */}
          <VisitorCounter />
        </div>

        {/* Navigation */}
        <div className="bg-gray-100 p-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {sections.map((section) => (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={`px-4 py-2 rounded-md text-sm font-medium capitalize ${
                  activeSection === section
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-200"
                }`}
              >
                {section}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Work Experience */}
          {shouldShowSection("experience") && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                Work Experience
              </h2>
              {experience.map((exp, index) => (
                <div key={index} className="mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold">{exp.company}</h3>
                      <p className="text-gray-600">{exp.position}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-600">{exp.location}</p>
                      <p className="text-gray-600">{exp.period}</p>
                    </div>
                  </div>
                  <ul className="list-disc list-inside mt-2 text-gray-700 text-sm">
                    {exp.achievements.map((achievement, idx) => (
                      <li key={idx} className="mb-1">
                        {achievement}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Research */}
          {shouldShowSection("research") && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                Research
              </h2>
              {research.map((res, index) => (
                <div key={index} className="mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold">
                        {res.organization}
                      </h3>
                      <p className="text-gray-600">{res.position}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-600">{res.period}</p>
                    </div>
                  </div>
                  <ul className="list-disc list-inside mt-2 text-gray-700 text-sm">
                    {res.details.map((detail, idx) => (
                      <li key={idx} className="mb-1">
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Conferences */}
          {shouldShowSection("conferences") && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                Conferences
              </h2>
              {conferences.map((conf, index) => (
                <div key={index} className="mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold">
                        <a
                          href={conf.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {conf.name}
                        </a>
                      </h3>
                      <p className="text-gray-600">{conf.role}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-600">{conf.location}</p>
                      <p className="text-gray-600">{conf.period}</p>
                    </div>
                  </div>
                  <p className="mt-2 text-gray-700 text-sm">
                    {conf.description}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Education */}
          {shouldShowSection("education") && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                Education
              </h2>
              {education.map((edu, index) => (
                <div key={index} className="mb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-semibold">
                        {edu.institution}
                      </h3>
                      <p className="text-gray-600">{edu.degree}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-gray-600">{edu.location}</p>
                      <p className="text-gray-600">{edu.period}</p>
                    </div>
                  </div>
                  {edu.details.length > 0 && (
                    <ul className="list-disc list-inside mt-2 text-gray-700 text-sm">
                      {edu.details.map((detail, idx) => (
                        <li key={idx} className="mb-1">
                          {typeof detail === "string" ? (
                            detail
                          ) : (
                            <>
                              {detail.text.split('"')[0]}"
                              <a
                                href={detail.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {detail.text.split('"')[1]}
                              </a>
                              "{detail.text.split('"')[2]}
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Skills */}
          {shouldShowSection("skills") && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                Skills
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-blue-600 mb-2">
                    Languages
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skills.languages.map((lang, index) => (
                      <span
                        key={index}
                        className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full text-sm"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-blue-600 mb-2">
                    Tech Stacks
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skills.Technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full text-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-blue-600 mb-2">
                    Libraries
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skills.libraries.map((lib, index) => (
                      <span
                        key={index}
                        className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full text-sm"
                      >
                        {lib}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-blue-600 mb-2">
                    Certifications
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {skills.certifications.map((cert, index) => (
                      <span
                        key={index}
                        className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full text-sm"
                      >
                        {typeof cert === "string" ? (
                          cert
                        ) : (
                          <a
                            href={cert.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {cert.name}
                          </a>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Projects */}
          {shouldShowSection("projects") && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 border-b border-gray-200 pb-2">
                Projects
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {projects.map((project, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-600">
                      {project.link ? (
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          {project.name}
                        </a>
                      ) : (
                        project.name
                      )}
                    </h3>
                    <p className="text-sm text-gray-700">
                      {project.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activities */}
          <div className="mt-6">
            <h3 className="font-semibold text-gray-800">
              Activities & Interests
            </h3>
            <div className="flex flex-wrap gap-2 mt-2">
              {activities.map((activity, index) => (
                <span
                  key={index}
                  className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                >
                  {activity}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resume;
